import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { ErrorNote, PageHeader, Spinner, useLoad } from "../components/ui"
import { ConnectStep } from "../components/migrations/batch/ConnectStep"
import { BatchImportStep, BatchPreviewStep, BatchReportStep, BatchWorking } from "../components/migrations/batch/RunSteps"
import { SelectStep } from "../components/migrations/batch/SelectStep"
import { UploadStep } from "../components/migrations/batch/UploadStep"
import { Stepper } from "../components/migrations/parts"
import { api } from "../lib/api"
import {
  BATCH_SOURCES,
  BATCH_STEPS,
  type Batch,
  type BatchSource,
  type TargetCompany,
  batchStep,
  isBatchBusy,
  isBatchFinal,
} from "../lib/batchMigrations"
import { startSingleFlightPoll } from "../lib/singleFlightPoll"

// Platform → Batch migration: every YouTrack project at once, each customer
// into its own company; or every BookStack shelf, each into a Context Hub
// space of its customer's company. There is one batch at a time per source;
// the page shows it, or the last one to finish until staff start another.

const BASE = "/platform/migrations/batches"

export function PlatformMigrationsPage() {
  const [params, setParams] = useSearchParams()
  const source: BatchSource = params.get("source") === "bookstack" ? "bookstack" : "youtrack"
  // Switching source remounts the wizard, so no state of one leaks into the other.
  return (
    <>
      <PageHeader
        title="Batch migration"
        subtitle={
          source === "bookstack"
            ? "Import BookStack shelves in one go, each shelf into a Context Hub space of its customer's company."
            : "Import YouTrack projects in one go, grouped by customer, each customer into its own company."
        }
      />
      <div className="mb-4 flex gap-1" role="tablist" aria-label="Source">
        {BATCH_SOURCES.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={s.id === source}
            className={`rounded-md px-3 py-1.5 text-sm ${
              s.id === source ? "bg-surface-muted font-medium text-foreground" : "text-muted hover:text-foreground"
            }`}
            onClick={() => setParams(s.id === "youtrack" ? {} : { source: s.id }, { replace: true })}
          >
            {s.name}
          </button>
        ))}
      </div>
      <BatchWizard key={source} source={source} />
    </>
  )
}

function BatchWizard({ source }: { source: BatchSource }) {
  const bookstack = source === "bookstack"
  const [batch, setBatch] = useState<Batch | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<unknown>(null)
  // A finished batch stays on screen until staff ask for a new one, and a
  // mapping file can be replaced from the selection step.
  const [fresh, setFresh] = useState(false)
  const [reupload, setReupload] = useState(false)
  const targets = useLoad(
    () => (batch ? api.get<{ companies: TargetCompany[] }>(`${BASE}/${batch.id}/targets`) : Promise.resolve({ companies: [] })),
    [batch?.id, batch?.status === "completed"],
  )
  const companies = targets.data?.companies ?? []

  useEffect(() => {
    let first = true
    return startSingleFlightPoll(async () => {
      if (!first && batch && !isBatchBusy(batch.status)) return
      first = false
      try {
        const path = batch ? `${BASE}/${batch.id}` : `${BASE}/current?source=${source}`
        const { batch: next } = await api.get<{ batch: Batch | null }>(path)
        setBatch(next)
        setError(null)
      } catch (err) {
        setError(err)
      } finally {
        setLoaded(true)
      }
    }, 2000)
    // Restart when the batch moves between busy and idle, so one that was
    // just started is followed again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch?.id, batch ? isBatchBusy(batch.status) : null])

  const cancel = async () => {
    if (!batch) return
    try {
      setBatch((await api.post<{ batch: Batch }>(`${BASE}/${batch.id}/cancel`)).batch)
    } catch (err) {
      setError(err)
    }
  }

  if (!loaded) return <Spinner />

  const shown = fresh || !batch ? null : batch
  const step = reupload ? 2 : batchStep(shown)

  return (
    <>
      <Stepper current={step} steps={BATCH_STEPS} />
      <ErrorNote error={error} />
      <ErrorNote error={targets.error} />
      {!shown && (
        <ConnectStep
          source={source}
          onCreated={(b) => {
            setFresh(false)
            setBatch(b)
          }}
        />
      )}
      {shown?.status === "scanning" && (
        <BatchWorking
          batch={shown}
          title={bookstack ? "Scanning BookStack" : "Scanning YouTrack"}
          intro={
            bookstack
              ? "Reading every shelf the token can see, with its books, and proposing a company for each."
              : "Reading every project the token can see, with its issues and agile boards."
          }
        />
      )}
      {shown?.status === "ready" && (step === 2 ? (
        <UploadStep
          batch={shown}
          onChange={(b) => {
            setReupload(false)
            setBatch(b)
          }}
          onCancel={cancel}
          onContinue={reupload ? () => setReupload(false) : undefined}
        />
      ) : (
        <SelectStep batch={shown} companies={companies} onChange={setBatch} onReupload={() => setReupload(true)} onCancel={cancel} />
      ))}
      {shown?.status === "previewing" && (
        <BatchWorking
          batch={shown}
          title="Preparing the preview"
          intro={
            bookstack
              ? "Counting the books and pages of each ticked shelf, and checking its space, without saving anything."
              : "Reading each ticked project and planning its import without saving anything."
          }
        />
      )}
      {shown?.status === "previewed" && <BatchPreviewStep batch={shown} companies={companies} onChange={setBatch} onCancel={cancel} />}
      {(shown?.status === "queued" || shown?.status === "running") && <BatchImportStep batch={shown} onChange={setBatch} />}
      {shown && isBatchFinal(shown.status) && <BatchReportStep batch={shown} companies={companies} onNew={() => setFresh(true)} />}
    </>
  )
}
